import { useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@clerk/clerk-react";
import { needsThemeBackfill, type ThemePreference } from "@fomofi/shared";
import { useProfile, useUpdateProfile } from "@/hooks/use-profile";

/**
 * AN-52 (Anthony, twice; Paul 2026-10-12): one theme setting, on the account, so a
 * choice made anywhere applies everywhere. next-themes still paints the first frame
 * from this browser's stored choice; the account's value wins once the profile loads.
 */

/** The theme shown now, and the one way to change it (all three controls use this). */
export function useAccountTheme(): {
  theme: ThemePreference;
  setAccountTheme: (theme: ThemePreference) => void;
} {
  const { resolvedTheme, setTheme } = useTheme();
  const updateProfile = useUpdateProfile();
  const { data: me } = useProfile();
  const theme: ThemePreference = resolvedTheme === "dark" ? "dark" : "light";
  const setAccountTheme = (next: ThemePreference) => {
    setTheme(next); // this device, at once
    // A demo persona keeps its theme on the device (Paul, 2026-10-12): it is shared,
    // and checks run on it one after another, so a stored theme would set the next
    // check's starting state. Mobile does the same (#1942).
    if (me?.is_demo === true) return;
    updateProfile.mutate({ profile: { theme: next } }); // the account, everywhere else
  };
  return { theme, setAccountTheme };
}

/**
 * Mounted once at the app root, active only when signed in (as PreferencesProvider is).
 * On each account's first profile load (a sign-in to another account without a reload
 * counts as a new one): an account with a theme makes this browser follow it; an account without one gets this browser's
 * current choice written up, so nobody's theme flips on the day this ships.
 */
export function AccountThemeSync() {
  const { resolvedTheme, setTheme } = useTheme();
  const { isSignedIn } = useAuth();
  const { data: me } = useProfile({ enabled: isSignedIn === true });
  const updateProfile = useUpdateProfile();
  const settledFor = useRef<number | null>(null);

  useEffect(() => {
    if (!isSignedIn || !me || !resolvedTheme || settledFor.current === me.id) return;
    settledFor.current = me.id;
    // A demo persona neither follows nor writes up the account's theme: device only.
    if (me.is_demo === true) return;
    const server = me.profile?.theme;
    if (needsThemeBackfill(server)) {
      updateProfile.mutate({ profile: { theme: resolvedTheme === "dark" ? "dark" : "light" } });
    } else if (server !== resolvedTheme) {
      setTheme(server as ThemePreference);
    }
    // Once per account by design: after this, the controls keep the two in step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, me, resolvedTheme]);

  return null;
}
